import { type Container, Graphics, Particle, ParticleContainer, Text } from 'pixi.js';
import { TILE_PX } from '../render/constants.js';
import type { AssetBundle, ParticleTextureName } from '../render/assets.js';
import { CRIT_COLOR, DAMAGE_COLORS, MAX_DAMAGE_NUMBERS } from './damageNumbers.js';
import { MAX_PARTICLES } from './particles.js';
import type { Vfx } from './vfx.js';

/**
 * Binds the VFX simulation to Pixi (TRD-04 §3).
 *
 * Two `ParticleContainer`s, one per blend mode. Additive is what gives energy and magic
 * hits their glow — the same particles under normal blending read as confetti.
 *
 * All 4096 particles are added to their container once and never removed; dead ones are
 * parked at alpha 0. `removeParticle` is O(n) and would turn every expiry into a churn
 * of array splices during exactly the frames that are already busiest.
 */
export class VfxRenderer {
  private readonly normal: ParticleContainer;
  private readonly additive: ParticleContainer;
  private readonly normalParticles: Particle[] = [];
  private readonly additiveParticles: Particle[] = [];

  private readonly numbers: Text[] = [];
  private readonly vignette = new Graphics();

  constructor(
    private readonly assets: AssetBundle,
    particleLayer: Container,
    numberLayer: Container,
    overlayLayer: Container,
    private readonly logicalW: number,
    private readonly logicalH: number,
  ) {
    const dynamic = { position: true, scale: true, rotation: true, color: true };
    this.normal = new ParticleContainer({ dynamicProperties: dynamic });
    this.additive = new ParticleContainer({ dynamicProperties: dynamic });
    this.additive.blendMode = 'add';

    const base = assets.particle.p_dot1;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const n = new Particle({ texture: base, alpha: 0 });
      n.anchorX = 0.5;
      n.anchorY = 0.5;
      this.normalParticles.push(n);
      this.normal.addParticle(n);

      const a = new Particle({ texture: base, alpha: 0 });
      a.anchorX = 0.5;
      a.anchorY = 0.5;
      this.additiveParticles.push(a);
      this.additive.addParticle(a);
    }

    particleLayer.addChild(this.normal, this.additive);

    for (let i = 0; i < MAX_DAMAGE_NUMBERS; i++) {
      const text = new Text({
        text: '',
        style: { fontFamily: 'monospace', fontSize: 8, fill: 0xffffff, fontWeight: 'bold' },
      });
      text.anchor.set(0.5, 0.5);
      text.visible = false;
      this.numbers.push(text);
      numberLayer.addChild(text);
    }

    overlayLayer.addChild(this.vignette);
  }

  sync(vfx: Vfx): void {
    this.syncParticles(vfx);
    this.syncNumbers(vfx);
    this.syncVignette(vfx);
  }

  private syncParticles(vfx: Vfx): void {
    const source = vfx.particles.particles;

    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = source[i];
      const normal = this.normalParticles[i] as Particle;
      const additive = this.additiveParticles[i] as Particle;

      if (!p || !p.active) {
        normal.alpha = 0;
        additive.alpha = 0;
        continue;
      }

      // A particle lives in exactly one container; the other is parked. Switching by
      // blend mode per particle is what lets one pool feed both.
      const target = p.blend === 'add' ? additive : normal;
      const other = p.blend === 'add' ? normal : additive;
      other.alpha = 0;

      target.texture = this.assets.particle[p.texture as ParticleTextureName];
      target.x = p.x * TILE_PX;
      target.y = p.y * TILE_PX;
      target.scaleX = p.currentSize;
      target.scaleY = p.currentSize;
      target.rotation = p.rotation;
      target.tint = p.currentTint;
      target.alpha = p.currentAlpha;
    }
  }

  private syncNumbers(vfx: Vfx): void {
    const items = vfx.damageNumbers.items;

    for (let i = 0; i < this.numbers.length; i++) {
      const n = items[i];
      const text = this.numbers[i] as Text;

      if (!n || !n.active) {
        text.visible = false;
        continue;
      }

      text.visible = true;
      text.text = String(Math.round(n.amount));
      text.x = Math.round(n.x * TILE_PX);
      text.y = Math.round(n.y * TILE_PX);
      text.alpha = Math.min(1, n.life / 260);

      // Crits are bigger AND gold; either alone is easy to miss in a busy frame.
      const scale = (n.crit ? 1.6 : 1) + n.pop * 0.35;
      text.scale.set(scale);
      text.style.fill = n.crit ? CRIT_COLOR : DAMAGE_COLORS[n.kind];
    }
  }

  private syncVignette(vfx: Vfx): void {
    this.vignette.clear();
    if (vfx.vignette <= 0) return;
    this.vignette
      .rect(0, 0, this.logicalW, this.logicalH)
      .fill({ color: 0xff006e, alpha: 0.25 * vfx.vignette });
  }

  destroy(): void {
    this.normal.destroy();
    this.additive.destroy();
    for (const t of this.numbers) t.destroy();
    this.vignette.destroy();
  }
}
